// audioPlayerStore.js — singleton audio controller for Intelligence briefs.
//
// Why this exists
// ---------------
// The Intelligence Brief audio used to be owned by the <BriefAudioPlayer>
// component. That meant the HTMLAudioElement unmounted with the component,
// so navigating to another tab (or backing out to the list) stopped
// playback. For a YouTube-style "keep going until I pause" experience we
// need a single audio element that outlives any React subtree.
//
// Architecture
// ------------
// - One module-level Audio() instance, created lazily on first play.
// - A tiny pub/sub so any component can useSyncExternalStore-style read
//   the current state (title, playing, t, dur, etc.) without prop drilling.
// - MediaSession API is wired on first play so iOS / Safari shows the
//   current brief on the lock screen and Control Center, with transport
//   controls. This is what makes playback feel native.
// - The actual iOS background playback privilege comes from two Xcode/
//   Capacitor pieces: UIBackgroundModes "audio" in Info.plist, and
//   AVAudioSession.setCategory(.playback) in AppDelegate. See those files.
//
// We intentionally do NOT tear down the element when the player UI
// unmounts — the whole point is that audio keeps playing. The only way to
// stop playback is:
//   - the user hits Pause (stays loaded, easy resume)
//   - the user hits the X on the mini bar (stop() — clears title + UI)
//   - audio ends naturally (stays loaded so they can replay)

let audioEl = null;
let currentBlobUrl = null;
let currentBriefId = null;

const listeners = new Set();

// True duration in seconds, precomputed from the MP3 frame count by the
// caller (see mp3Duration.js). We prefer this over anything the HTMLAudio
// element reports, because WKWebView's estimate can be dramatically wrong
// on VBR MP3s — see the comment in mp3Duration.js. 0 means "we don't have
// a probe; trust the element".
let preciseDuration = 0;

// Wall-clock anchor for progress tracking. When currentTime is advancing
// normally we mirror it; when WebKit clamps currentTime at a wrong
// duration (same VBR MP3 bug), currentTime freezes and the scrubber would
// otherwise freeze with it even though the audio keeps playing. The
// anchor lets us derive a wall-clock t that keeps advancing while
// `state.playing === true`, and we take the max of the two sources so
// correct-but-paused cases also work.
//
// Re-anchor whenever we start, seek, or change speed.
let playAnchor = null; // { wall: number, audio: number } | null

function anchorNow() {
  if (!audioEl) { playAnchor = null; return; }
  // Anchor off state.t — our source of truth. Reading audioEl.currentTime
  // here would be wrong whenever the user just seeked-while-paused and
  // WKWebView hasn't landed the seek yet (the element still shows 0 or a
  // clamped value), or after 'ended' when the element resets currentTime
  // to its own lying duration. state.t already incorporates user intent
  // via seek() and correct playback via the tick, so it's the safest base.
  const elT = audioEl.currentTime || 0;
  playAnchor = {
    wall: typeof performance !== 'undefined' ? performance.now() : Date.now(),
    audio: Math.max(state.t || 0, elT),
  };
}

function wallClockT() {
  if (!playAnchor) return 0;
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  return playAnchor.audio + ((now - playAnchor.wall) / 1000) * (state.speed || 1);
}

// Public state — everything a UI needs to render the player.
const state = {
  // 'idle' | 'loading' | 'ready' | 'error'
  status: 'idle',
  error: '',
  // Metadata for the currently-loaded brief. Null when no audio is loaded.
  brief: null,
  playing: false,
  t: 0,
  dur: 0,
  speed: 1,
};

// CACHED snapshot for useSyncExternalStore. The getSnapshot callback must
// return a referentially stable value when the state hasn't changed —
// otherwise React's SES bails into an infinite re-render loop (each render
// sees a "new object", triggers another render, …) and eventually throws
// "Maximum update depth exceeded", which on iOS WKWebView manifests as a
// black screen because the bundle never finishes committing. We therefore
// only mint a new snapshot inside emit(), after a real mutation.
let snapshot = { ...state };
function refreshSnapshot() { snapshot = { ...state }; }

function emit() {
  refreshSnapshot();
  listeners.forEach(fn => {
    try { fn(snapshot); } catch (e) { /* keep other subs alive */ }
  });
}

// A usable duration is a finite, positive number. TTS blobs often report
// Infinity until the decoder has seen the whole stream, and an uninitialized
// element reports NaN — both of those would make the scrubber math degenerate
// (pct = t / Infinity = 0, or NaN), so we gate every duration write through
// this helper and leave state.dur untouched until the browser gives us a real
// number.
function readUsableDuration(el) {
  const d = el?.duration;
  return (typeof d === 'number' && isFinite(d) && d > 0) ? d : 0;
}

// Pick the best estimate for the duration — the precomputed MP3 probe if we
// have one (ground truth, regardless of VBR/CBR), else the live element
// reading clamped upward so it can never shrink below what we've played.
function bestDuration(elDur, observedT) {
  return Math.max(preciseDuration || 0, elDur || 0, observedT || 0, state.dur || 0);
}

// Pick the best estimate for the current position. WKWebView can clamp
// `currentTime` at a mis-reported duration on VBR MP3s, freezing the
// scrubber at (e.g.) 3:49 while audio keeps playing. The wall-clock
// fallback keeps t advancing through real playback so the scrubber tracks
// what the user is hearing. We still take the max, so when currentTime is
// honest (most cases) we prefer it verbatim.
function bestPosition() {
  const elT = audioEl ? (audioEl.currentTime || 0) : 0;
  const wallT = state.playing ? wallClockT() : 0;
  let t = Math.max(elT, wallT);
  // Never advance past the known end.
  const cap = bestDuration(readUsableDuration(audioEl), t);
  if (cap > 0) t = Math.min(t, cap);
  return t;
}

// rAF-driven tick for the progress bar. The native `timeupdate` event only
// fires ~4Hz, so a CSS-driven width interpolation looks choppy. Ticking on
// rAF while playing keeps the bar gliding smoothly even though the underlying
// truth source is still the audio element.
let rafHandle = 0;
function startRafTick() {
  if (rafHandle) return;
  const tick = () => {
    rafHandle = 0;
    if (!audioEl || audioEl.paused) return;
    const nextT = bestPosition();
    const liveDur = readUsableDuration(audioEl);
    const correctedDur = bestDuration(liveDur, nextT);
    let changed = false;
    if (nextT !== state.t) { state.t = nextT; changed = true; }
    if (correctedDur !== state.dur) { state.dur = correctedDur; changed = true; }
    if (changed) emit();
    rafHandle = requestAnimationFrame(tick);
  };
  rafHandle = requestAnimationFrame(tick);
}
function stopRafTick() {
  if (rafHandle) { cancelAnimationFrame(rafHandle); rafHandle = 0; }
}

function ensureAudio() {
  if (audioEl) return audioEl;
  audioEl = new Audio();
  audioEl.preload = 'metadata';
  // iOS Safari / WKWebView needs playsinline to keep from hijacking into
  // fullscreen video controls for audio-only elements. Harmless elsewhere.
  audioEl.setAttribute('playsinline', 'true');
  audioEl.setAttribute('webkit-playsinline', 'true');

  audioEl.addEventListener('play',       () => {
    state.playing = true;
    anchorNow();                                    // wall-clock starts now
    emit(); updateMediaSessionPlayback(); startRafTick();
  });
  audioEl.addEventListener('pause',      () => {
    state.playing = false;
    playAnchor = null;                              // freeze wall-clock
    emit(); updateMediaSessionPlayback(); stopRafTick();
  });
  audioEl.addEventListener('ended',      () => {
    state.playing = false;
    playAnchor = null;
    // Lock to the best known duration so the final readout is honest.
    const liveDur = readUsableDuration(audioEl);
    const finalDur = bestDuration(liveDur, bestPosition());
    if (finalDur > 0) { state.dur = finalDur; state.t = finalDur; }
    emit();
    updateMediaSessionPlayback();
    stopRafTick();
  });
  audioEl.addEventListener('seeking',    () => { anchorNow(); });
  audioEl.addEventListener('seeked',     () => { anchorNow(); });
  audioEl.addEventListener('ratechange', () => { anchorNow(); });
  audioEl.addEventListener('timeupdate', () => {
    // Only update when actually playing. When paused, state.t encodes user
    // intent (where the scrubber was last pointed), and WKWebView's
    // clamped currentTime would otherwise drag it back and make the thumb
    // snap to the fake-duration ceiling mid-seek.
    if (!state.playing) {
      // But we CAN still grow dur if the element happened to report more.
      const liveDur = readUsableDuration(audioEl);
      const correctedDur = bestDuration(liveDur, state.t);
      if (correctedDur !== state.dur) { state.dur = correctedDur; emit(); }
      return;
    }
    const nextT = bestPosition();
    const liveDur = readUsableDuration(audioEl);
    const correctedDur = bestDuration(liveDur, nextT);
    let changed = false;
    if (nextT !== state.t) { state.t = nextT; changed = true; }
    if (correctedDur !== state.dur) { state.dur = correctedDur; changed = true; }
    if (changed) emit();
  });
  // Duration can land via either `loadedmetadata` (initial) or `durationchange`
  // (when the decoder settles on a final length after a streaming blob finishes
  // loading, or when a range request resolves on WKWebView). Listen for both,
  // and only accept finite positive values — otherwise we'd overwrite a good
  // duration with Infinity mid-playback. We still defer to preciseDuration
  // when present via bestDuration().
  const onDur = () => {
    const d = readUsableDuration(audioEl);
    const next = bestDuration(d, state.t);
    if (next > 0 && next !== state.dur) { state.dur = next; emit(); updateMediaSessionPlayback(); }
  };
  audioEl.addEventListener('loadedmetadata', onDur);
  audioEl.addEventListener('durationchange', onDur);
  audioEl.addEventListener('canplay',        onDur);
  audioEl.addEventListener('error',      () => { state.status = 'error'; state.error = 'Playback error'; emit(); stopRafTick(); });

  return audioEl;
}

// Wire the browser MediaSession so iOS shows the now-playing card on the
// lock screen and in Control Center. Without this, background audio works
// but looks like it came out of nowhere.
function wireMediaSession(brief) {
  if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
  try {
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: brief?.title || 'The Briefing',
      artist: 'Proxera Intelligence',
      album: brief?.bundle ? brief.bundle.toString() : 'Briefing',
      // 512x512 artwork would live here if we had one cached locally.
    });
    navigator.mediaSession.setActionHandler('play',           () => play());
    navigator.mediaSession.setActionHandler('pause',          () => pause());
    navigator.mediaSession.setActionHandler('seekbackward',   () => skip(-15));
    navigator.mediaSession.setActionHandler('seekforward',    () => skip(15));
    navigator.mediaSession.setActionHandler('seekto',         (d) => { if (d?.seekTime != null) seek(d.seekTime); });
    navigator.mediaSession.setActionHandler('stop',           () => stop());
  } catch {}
}

function updateMediaSessionPlayback() {
  if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
  try {
    navigator.mediaSession.playbackState = state.playing ? 'playing' : 'paused';
    if (typeof navigator.mediaSession.setPositionState === 'function' && isFinite(state.dur) && state.dur > 0) {
      navigator.mediaSession.setPositionState({
        duration: state.dur,
        position: Math.min(state.t, state.dur),
        playbackRate: state.speed,
      });
    }
  } catch {}
}

// ── Public API ──────────────────────────────────────────────────────

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getState() {
  // Return the cached snapshot, NOT a fresh copy. See the comment above.
  return snapshot;
}

export function isSameBrief(briefId) {
  return currentBriefId && currentBriefId === briefId;
}

// Load a brief's audio blob and start playing. If the same brief is
// already loaded, we just resume where we left off.
//
// `preciseDuration` (optional, seconds) is a ground-truth duration the
// caller has precomputed by parsing the MP3 frames. We trust it over the
// element's reported duration — see the comment at the top of
// mp3Duration.js. Pass 0 or omit if unknown.
export async function loadAndPlay({ brief, blobUrl, speed = 1, preciseDuration: pd = 0 }) {
  const el = ensureAudio();
  if (currentBriefId === brief?.id && currentBlobUrl === blobUrl) {
    // Same source — just play. Do NOT reset t/dur; the element already knows
    // them and resetting would flash 0:00 / 0:00 for a frame.
    el.playbackRate = speed;
    state.speed = speed;
    state.status = 'ready';
    state.brief = brief;
    if (pd > 0) preciseDuration = pd;               // upgrade if we just got a probe
    const d = readUsableDuration(el);
    const dur = bestDuration(d, state.t);
    if (dur > 0) state.dur = dur;
    state.t = el.currentTime || state.t || 0;
    emit();
    await el.play().catch(() => {});
    return;
  }
  currentBlobUrl = blobUrl;
  currentBriefId = brief?.id || null;
  preciseDuration = pd > 0 ? pd : 0;
  el.src = blobUrl;
  el.playbackRate = speed;
  state.speed = speed;
  state.status = 'ready';
  state.brief = brief;
  state.t = 0;
  state.dur = preciseDuration;   // seed with probe; element will refine if it can
  state.error = '';
  emit();
  wireMediaSession(brief);
  await el.play().catch(() => {});
}

export function attachExisting({ brief, blobUrl, speed = 1, preciseDuration: pd = 0 }) {
  // Used when audio was prepared elsewhere but not yet playing — we adopt
  // it without autoplay so the UI shows a "ready" pill the user can press.
  //
  // Crucially: if we're re-adopting the SAME blob the store already had
  // (common when the user navigates away from the brief detail page and
  // comes back), don't reassign el.src — in some WKWebView builds that
  // resets currentTime to 0 and never re-fires loadedmetadata, leaving the
  // scrubber stuck. Just refresh the brief pointer and re-emit.
  const el = ensureAudio();
  const sameSource = currentBriefId === (brief?.id || null) && currentBlobUrl === blobUrl && el.src;
  if (sameSource) {
    el.playbackRate = speed;
    state.speed = speed;
    state.status = 'ready';
    state.brief = brief;
    if (pd > 0) preciseDuration = pd;
    const d = readUsableDuration(el);
    const dur = bestDuration(d, state.t);
    if (dur > 0) state.dur = dur;
    state.t = el.currentTime || state.t || 0;
    emit();
    return;
  }
  currentBlobUrl = blobUrl;
  currentBriefId = brief?.id || null;
  preciseDuration = pd > 0 ? pd : 0;
  el.src = blobUrl;
  el.playbackRate = speed;
  state.speed = speed;
  state.status = 'ready';
  state.brief = brief;
  state.t = 0;
  state.dur = preciseDuration;
  state.error = '';
  emit();
  wireMediaSession(brief);
}

export function play() {
  const el = ensureAudio();
  if (!el.src) return;
  el.playbackRate = state.speed;
  // Two failure modes we defend against here:
  //  1. WKWebView unloads the AVPlayer buffer on pause for blob audio
  //     URLs, so el.currentTime snaps to 0 while state.t still holds the
  //     user's last position. Calling play() verbatim would restart from
  //     the beginning.
  //  2. The user scrubbed while paused. WKWebView sometimes defers the
  //     actual seek until playback begins, and in the mean-time can land
  //     at 0 or the wrong clamped duration.
  // In both cases we want the audio to resume from state.t.
  const elT = el.currentTime || 0;
  // Reseat target must stay under WK's reported duration — otherwise the
  // reseat itself fires `ended` (same VBR clamp bug as seek()).
  const reseatTarget = clampSeekToElement(el, state.t);
  const needsReseat = reseatTarget > 0.25 && Math.abs(reseatTarget - elT) > 0.25;
  if (needsReseat) {
    try { el.currentTime = reseatTarget; } catch {}
  }
  const p = el.play();
  if (p && typeof p.then === 'function') {
    p.then(() => {
      // One more correction pass after play() resolves — WKWebView has
      // been observed to snap currentTime to 0 during the play transition
      // specifically after a prior 'ended' event. Compare against state.t
      // (the user's intent, which the tick is otherwise about to grow
      // via wall-clock) and force the seek if reality drifted.
      try {
        const nowT = el.currentTime || 0;
        const tgt = clampSeekToElement(el, state.t);
        if (tgt > 0.25 && Math.abs(tgt - nowT) > 0.5) {
          el.currentTime = tgt;
          anchorNow();
        }
      } catch {}
    }).catch(() => {});
  }
}

export function pause() {
  const el = ensureAudio();
  el.pause();
}

export function toggle() {
  const el = ensureAudio();
  if (!el.src) return;
  if (el.paused) play();
  else pause();
}

// Bracket the `el.currentTime` assignment so we never cross WKWebView's
// reported duration. On VBR MP3s WK underestimates duration (e.g. 3:50 on a
// 6:48 file). Natural playback crosses that boundary fine, but any
// programmatic seek past it is treated as end-of-stream: WK fires `ended`,
// which used to peg state.t to finalDur and — once fixed — would leave the
// scrubber visually past the end with audio stopped. Keeping a hair of
// headroom below `el.duration` sidesteps the problem entirely.
//
// IMPORTANT: only clamp when el.duration is *also* the best duration we
// have. When preciseDuration (our frame-walked probe) is larger, that
// means WebKit is still lying about the stream length — clamping to its
// lie would pin resume/seek targets inside the underestimate and produce
// the "resume jumps back to insight 3" / "scrubber caps at 3:50" bug.
// Honour the user's target up to preciseDuration, then let WebKit's own
// currentTime assignment fail loudly rather than silently clipping.
function clampSeekToElement(el, target) {
  const liveDur = readUsableDuration(el);
  const truthDur = preciseDuration || 0;
  // If our probe knows the stream is longer than WebKit thinks, trust the
  // probe. WebKit will accept a currentTime greater than its reported
  // duration on VBR MP3s as long as the actual decoded frames are there —
  // what breaks is when target > spec-clamp duration AND we're trying to
  // land ON or past it (that fires `ended`). Using preciseDuration as the
  // ceiling keeps `target` inside real audio and avoids the end-of-stream
  // trip.
  if (truthDur > 0 && truthDur > liveDur + 0.5) {
    return Math.max(0, Math.min(target, truthDur - 0.3));
  }
  if (liveDur > 0 && target > liveDur - 0.3) {
    return Math.max(0, liveDur - 0.3);
  }
  return Math.max(0, target);
}

export function seek(seconds) {
  const el = ensureAudio();
  // Use the grown state.dur as the ceiling too — on VBR MP3s the element's
  // duration can be shorter than what's really there, and clamping to it
  // would block the user from seeking near the actual end.
  const d = bestDuration(readUsableDuration(el), state.t);
  if (!el || d === 0) return;
  const target = Math.max(0, Math.min(d, seconds));
  // `state.t` tracks the user's intent (matches the scrubber thumb).
  // `el.currentTime` must stay under WK's reported duration to avoid the
  // seek-past-end / ended-on-commit bug. When target > liveDur they
  // diverge — natural playback will let the element catch up.
  const elTarget = clampSeekToElement(el, target);
  el.currentTime = elTarget;
  state.t = elTarget;                               // snap scrubber to the
                                                    // furthest we can truly go
  anchorNow();                                      // re-anchor wall-clock
  emit();
}

export function skip(delta) {
  const el = ensureAudio();
  const d = bestDuration(readUsableDuration(el), state.t);
  if (!el || d === 0) return;
  // Skip from our best-known position, not the element's (which may be
  // clamped). Then write back to the element; browsers that honor the
  // seek will jump, browsers that don't will still have wall-clock t.
  const target = Math.max(0, Math.min(d, state.t + delta));
  const elTarget = clampSeekToElement(el, target);
  el.currentTime = elTarget;
  state.t = elTarget;
  anchorNow();
  emit();
}

export function setSpeed(next) {
  const el = ensureAudio();
  state.speed = next;
  el.playbackRate = next;
  anchorNow();                                      // re-anchor at new rate
  emit();
  updateMediaSessionPlayback();
}

// ── Audio ducking ───────────────────────────────────────────────────
// When another feature needs the mic or priority audio (e.g. the user
// opens the Daily Briefing recorder while a brief is playing), call
// duck() to temporarily pause us, and unduck() when done. Semantics:
//
//  - duck() is idempotent: repeated calls are a no-op until unduck().
//  - We only resume on unduck() if WE paused it. If the user had already
//    paused manually, we leave it paused. If the user hits Stop (X) while
//    ducked, we clear our resume intent too.
//  - The mini bar and metadata stay loaded during a duck — this is a
//    temporary pause, not a stop.
let duckedByUs = false;
let wasPlayingWhenDucked = false;

export function duck() {
  if (duckedByUs) return;           // already ducked, nothing to do
  if (!audioEl) return;             // no audio loaded — nothing to duck
  wasPlayingWhenDucked = state.playing === true;
  duckedByUs = true;
  if (wasPlayingWhenDucked) {
    try { audioEl.pause(); } catch {}
  }
}

export function unduck() {
  if (!duckedByUs) return;
  const shouldResume = wasPlayingWhenDucked;
  duckedByUs = false;
  wasPlayingWhenDucked = false;
  if (!shouldResume) return;
  if (!audioEl || !audioEl.src) return;   // got stopped while ducked
  if (!state.brief) return;
  try { audioEl.play().catch(() => {}); } catch {}
}

// Full stop — clears the mini bar too. Pause is the soft version that
// leaves the bar up so the user can resume.
export function stop() {
  // Clear any pending duck-resume intent so we don't try to resume a
  // brief the user just dismissed.
  duckedByUs = false;
  wasPlayingWhenDucked = false;
  if (audioEl) {
    try { audioEl.pause(); audioEl.src = ''; } catch {}
  }
  currentBlobUrl = null;
  currentBriefId = null;
  preciseDuration = 0;
  playAnchor = null;
  state.status = 'idle';
  state.brief = null;
  state.playing = false;
  state.t = 0;
  state.dur = 0;
  state.error = '';
  emit();
  if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
    try {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = 'none';
    } catch {}
  }
}

// Mark status loading while we fetch a blob. Kept separate from
// loadAndPlay so the UI can show a spinner without owning the fetch.
export function markLoading(brief) {
  state.status = 'loading';
  state.brief = brief;
  state.error = '';
  emit();
}

export function markError(msg) {
  state.status = 'error';
  state.error = msg || 'Audio failed.';
  emit();
}
